import { ReactNode } from "react";
import { cn } from "../../lib/utils";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/common/sheet";

export interface TailwindSideModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  title?: string;
  /** Small uppercase label above the title (e.g. "LCA products"). Omit for the default look — every existing caller keeps rendering exactly as before. */
  eyebrow?: string;
  description?: ReactNode;
  testId?: string;
  contentClassName?: string;
  headerClassName?: string;
  /** Extra classes merged onto the title, e.g. `tw-font-display` for the LCA screens. Existing callers are unaffected unless they pass this. */
  titleClassName?: string;
  /** Extra content directly under the title (e.g. a type badge + register number row). Omit for the default look. */
  titleMeta?: ReactNode;
  /**
   * Right-aligned header content next to the title block (e.g. a sales-unit
   * selector) — switches the header to a two-column row instead of the
   * default single centered column. Omit for the default look — every
   * existing caller keeps rendering exactly as before. Leave room for the
   * Sheet's own absolute-positioned close button (top-right) in whatever
   * you pass here, same as the Lovable prototype does with a trailing
   * spacer.
   */
  headerRight?: ReactNode;
  /** Forwarded to the underlying Sheet/Dialog content, e.g. to prevent the default auto-focus behavior on open. */
  onOpenAutoFocus?: (event: Event) => void;
}

const TailwindSideModal = ({
  open,
  onOpenChange,
  children,
  title,
  eyebrow,
  description,
  testId,
  contentClassName,
  headerClassName,
  titleClassName,
  titleMeta,
  headerRight,
  onOpenAutoFocus
}: TailwindSideModalProps) => {
  const titleBlock = (
    <>
      {eyebrow ? (
        <p className="tw-mb-0.5 tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wider tw-text-muted-foreground">
          {eyebrow}
        </p>
      ) : null}
      <SheetTitle className={cn("tw-text-lg tw-font-bold tw-text-foreground", titleClassName)}>
        {title}
      </SheetTitle>
      {titleMeta ? <div className="tw-mt-1">{titleMeta}</div> : null}
      {description ? <SheetDescription>{description}</SheetDescription> : null}
    </>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid={testId}
        onOpenAutoFocus={onOpenAutoFocus}
        className={cn("!tw-w-full sm:!tw-max-w-6xl !tw-p-0 tw-flex tw-flex-col", contentClassName)}
      >
        <SheetHeader
          className={cn(
            "tw-shrink-0 tw-border-b tw-border-border tw-px-6 tw-pt-6 tw-pb-2",
            headerRight
              ? "tw-flex-row tw-items-start tw-justify-between tw-space-y-0 tw-py-4 tw-text-left"
              : "",
            headerClassName
          )}
        >
          {headerRight ? (
            <>
              <div className="tw-min-w-0">{titleBlock}</div>
              <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-3">{headerRight}</div>
            </>
          ) : (
            titleBlock
          )}
        </SheetHeader>
        {children}
      </SheetContent>
    </Sheet>
  );
};

export default TailwindSideModal;
